/**
 * Custom hook for managing collegiate fantasy data fetching
 * Handles data fetching, state management, and loading states
 */

import { useState, useEffect, useRef } from "react";
import { fetchCollegiateFantasyData, DataFilterValues } from "../services/dataService";
import { conferenceData } from "../data/conferenceData";

interface Worker {
  applyDataFilters?: (params: any) => Promise<any>;
}

interface UseCollegiateDataOptions {
  year: number;
  statFilter?: string;
  conferenceView?: boolean;
  worker?: Worker | null;
  dataFilters?: DataFilterValues | null;
}

/**
 * Custom hook for fetching and managing collegiate fantasy data
 * @param options - Configuration options
 * @returns Data and state management functions
 */
export const useCollegiateData = ({
  year,
  statFilter = "",
  conferenceView = false,
  worker,
  dataFilters = null,
}: UseCollegiateDataOptions) => {
  // Primitive key so a new object with the same filters doesn't refetch
  const filtersKey = JSON.stringify(dataFilters ?? null);
  const [collegiateFantasyData, setCollegiateFantasyData] = useState<any>(null);
  const [collegiateConferenceData, setCollegiateConferenceData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalWeeks, setTotalWeeks] = useState<number>(0);

  // Stale-while-revalidate: keep showing prior payload on stat/filter changes.
  // Year change clears so Loading shows instead of the wrong season.
  const hasDataRef = useRef(false);
  const prevYearRef = useRef<number | null>(null);

  // Fetch conference data when conference view is enabled
  useEffect(() => {
    if (conferenceView) {
      setCollegiateConferenceData(conferenceData);
    } else {
      setCollegiateConferenceData(null);
    }
  }, [conferenceView]);

  // Fetch collegiate fantasy data when year, statFilter or the player filters change
  useEffect(() => {
    let isCancelled = false;

    const yearChanged =
      prevYearRef.current !== null && prevYearRef.current !== year;
    prevYearRef.current = year;

    if (yearChanged) {
      hasDataRef.current = false;
      setCollegiateFantasyData(null);
    }

    const loadData = async () => {
      // Only flash Loading on first load or after a year change
      if (!hasDataRef.current) {
        setLoading(true);
      }
      setError(null);

      try {
        const filters: DataFilterValues | null = JSON.parse(filtersKey);
        const result = await fetchCollegiateFantasyData(year, statFilter, worker || null, filters);

        if (!isCancelled) {
          setCollegiateFantasyData(result);
          hasDataRef.current = true;
          if (result && result.data) {
            setTotalWeeks(Object.keys(result.data).length);
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          console.error("Error loading collegiate data:", err);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [year, statFilter, worker, filtersKey]);

  return {
    collegiateFantasyData,
    collegiateConferenceData,
    loading,
    error,
    totalWeeks,
  };
};
